/**
 * Installation brand for the Agent interface, read once from the server's
 * bootstrap attributes before the first render. Components and messages read
 * names from here instead of hard-coding a product. The fallback is the
 * default OpenCloud brand, used by unit tests and by documents without brand
 * attributes.
 */
export interface AgentBrand {
  /** Product name shown to people, e.g. in titles and status messages. */
  readonly productName: string;
  /** How the hosted Agent is labelled. */
  readonly agentName: string;
  /** `data-oc-theme` value for themed surfaces, including portaled ones. */
  readonly themeId: string;
}

export const defaultAgentBrand: AgentBrand = Object.freeze({
  productName: "OpenCloud",
  agentName: "Agent",
  themeId: "opencloud",
});

let current: AgentBrand = defaultAgentBrand;

export function setAgentBrand(brand: AgentBrand): void {
  current = Object.freeze({ ...brand });
}

export function agentBrand(): AgentBrand {
  return current;
}

const displayNamePattern = /^[A-Za-z0-9][A-Za-z0-9 .-]{0,39}$/;
const themeIdPattern = /^[a-z][a-z0-9-]{1,30}$/;

/** Reads brand attributes, keeping the default for any missing or invalid value. */
export function readAgentBrand(dataset: DOMStringMap): AgentBrand {
  const { productName, agentName, theme } = dataset;
  return {
    productName:
      productName && displayNamePattern.test(productName)
        ? productName
        : defaultAgentBrand.productName,
    agentName:
      agentName && displayNamePattern.test(agentName)
        ? agentName
        : defaultAgentBrand.agentName,
    themeId:
      theme && themeIdPattern.test(theme) ? theme : defaultAgentBrand.themeId,
  };
}
